import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { BarChart3, Users, DollarSign, ShieldCheck, LogOut } from 'lucide-react';
import { AdminDashboard } from './admin/AdminDashboard';
import { AdminUsers } from './admin/AdminUsers';
import { AdminSubscriptions } from './admin/AdminSubscriptions';

const LIMIT = 15;

const AdminPage = () => {
  const { api, user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [activeTab, setActiveTab] = useState('dashboard');

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Acc\u00e8s non autoris\u00e9");
        navigate('/app');
      }
    }
  }, [api, navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('skip', String(page * LIMIT));
      params.set('limit', String(LIMIT));
      if (searchTerm) params.set('search', searchTerm);
      if (planFilter !== 'ALL') params.set('plan_filter', planFilter);
      if (statusFilter) params.set('status_filter', statusFilter);
      const res = await api.get(`/admin/users?${params}`);
      setUsers(res.data.users);
      setTotalUsers(res.data.total);
    } catch (err) {
      console.error(err);
    }
  }, [api, page, searchTerm, planFilter, statusFilter]);

  useEffect(() => {
    Promise.all([fetchStats(), fetchUsers()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <header className="sticky top-0 z-40 glass border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold font-['Manrope'] leading-tight">Administration</h1>
              <p className="text-xs text-muted-foreground">IziCatalog</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-1" /> {"D\u00e9connexion"}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6" data-testid="admin-tabs">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard"><BarChart3 className="w-4 h-4 mr-1.5" /> Tableau de bord</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 mr-1.5" /> Utilisateurs</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions"><DollarSign className="w-4 h-4 mr-1.5" /> Abonnements</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <AdminDashboard stats={stats} />
          </TabsContent>

          <TabsContent value="users">
            <AdminUsers
              api={api}
              users={users}
              setUsers={setUsers}
              totalUsers={totalUsers}
              page={page}
              setPage={setPage}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              planFilter={planFilter}
              setPlanFilter={setPlanFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              fetchUsers={fetchUsers}
              fetchStats={fetchStats}
            />
          </TabsContent>

          <TabsContent value="subscriptions">
            <AdminSubscriptions api={api} users={users} setUsers={setUsers} stats={stats} fetchStats={fetchStats} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
